import type { ResultOf } from '@horof/shared';
import { graphql } from './gql';

/** Members and meters on the desk (docs/changes/41). */

export const MembersDocument = graphql(`
  query DeskMembers {
    members(orderBy: [LAST_NAME_ASC, FIRST_NAME_ASC]) {
      nodes {
        id
        fullName
        memberAddress
        phoneStatus
        notifyChannel
        notifyPhone
        committeeRole
        email
        status
        meters(orderBy: [SORT_ORDER_ASC]) {
          nodes {
            id
            label
            meterStatus
          }
        }
        unnamedShart: shartPeople(
          filter: { name: { equalTo: "شخص غير مسمى" } }
        ) {
          totalCount
        }
      }
    }
  }
`);
export type MemberRow = NonNullable<
  NonNullable<ResultOf<typeof MembersDocument>['members']>['nodes'][number]
>;

export const MemberDocument = graphql(`
  query DeskMember($id: UUID!) {
    member(id: $id) {
      id
      fullName
      firstName
      fatherName
      lastName
      sex
      memberAddress
      phone
      contactPhone
      contactName
      notifyChannel
      notifyPhone
      phoneStatus
      email
      committeeRole
      status
      leftAt
      leftReason
      joinedAt
      statementLink
      noticeLink
      meters(orderBy: [SORT_ORDER_ASC]) {
        nodes {
          id
          label
          meterAddress
          meterStatus
        }
      }
      unpaid: bills(filter: { billStatus: { notEqualTo: PAID } }) {
        totalCount
      }
      shartPeople(orderBy: [CREATED_AT_ASC]) {
        nodes {
          id
          name
        }
      }
      memberStatusChanges(orderBy: [CHANGED_AT_DESC]) {
        nodes {
          id
          fromStatus
          toStatus
          reason
          changedOn
          changedBy
        }
      }
    }
  }
`);
export type MemberDetails = NonNullable<
  ResultOf<typeof MemberDocument>['member']
>;

export const CreateMemberDocument = graphql(`
  mutation DeskCreateMember($member: MemberInput!) {
    createMember(input: { member: $member }) {
      member {
        id
      }
    }
  }
`);

export const UpdateMemberDocument = graphql(`
  mutation DeskUpdateMember($id: UUID!, $patch: MemberPatch!) {
    updateMember(input: { id: $id, patch: $patch }) {
      member {
        id
        fullName
        firstName
        fatherName
        lastName
        sex
        memberAddress
        phone
        contactPhone
        contactName
        notifyChannel
        notifyPhone
        phoneStatus
        email
        committeeRole
      }
    }
  }
`);

export const ChangeMemberStatusDocument = graphql(`
  mutation DeskChangeMemberStatus($input: AppChangeMemberStatusInput!) {
    appChangeMemberStatus(input: $input) {
      clientMutationId
    }
  }
`);

export const CreateShartPersonDocument = graphql(`
  mutation DeskCreateShartPerson($memberId: UUID!, $name: String!) {
    createShartPerson(
      input: { shartPerson: { memberId: $memberId, name: $name } }
    ) {
      shartPerson {
        id
        name
      }
    }
  }
`);

export const RenameShartPersonDocument = graphql(`
  mutation DeskRenameShartPerson($id: UUID!, $name: String!) {
    updateShartPerson(input: { id: $id, patch: { name: $name } }) {
      shartPerson {
        id
        name
      }
    }
  }
`);

export const DeleteShartPersonDocument = graphql(`
  mutation DeskDeleteShartPerson($id: UUID!) {
    deleteShartPerson(input: { id: $id }) {
      shartPerson {
        id
      }
    }
  }
`);

export const MetersDocument = graphql(`
  query DeskMeters {
    meters(orderBy: [SORT_ORDER_ASC, METER_NO_ASC]) {
      nodes {
        id
        label
        meterNo
        meterAddress
        meterStatus
        sortOrder
        photo {
          id
        }
        member {
          id
          fullName
        }
      }
    }
  }
`);
export type MeterRow = NonNullable<
  NonNullable<ResultOf<typeof MetersDocument>['meters']>['nodes'][number]
>;

export const MeterDocument = graphql(`
  query DeskMeter($id: UUID!) {
    meter(id: $id) {
      id
      label
      meterNo
      meterAddress
      meterStatus
      statusReason
      statusChangedAt
      connectedAt
      memberId
      photo {
        id
        url
        contentType
        createdAt
        uploadedBy
      }
      member {
        id
        fullName
        meters(filter: { meterStatus: { equalTo: CONNECTED } }) {
          nodes {
            id
            label
          }
        }
      }
      meterStatusChanges(orderBy: [CHANGED_AT_DESC], first: 20) {
        nodes {
          id
          fromStatus
          toStatus
          reason
          changedOn
          changedBy
          reading
          meterByReplacedBy {
            label
          }
        }
      }
      recentBills: bills(orderBy: [BILL_MONTH_DESC], first: 12) {
        nodes {
          id
          billMonth
          currentReading
          consumption
          billTotal
          billStatus
          readingPhoto {
            id
            url
          }
        }
      }
    }
  }
`);
export type MeterDetails = NonNullable<ResultOf<typeof MeterDocument>['meter']>;

export const MemberChoicesDocument = graphql(`
  query DeskMemberChoices {
    members(
      orderBy: [LAST_NAME_ASC, FIRST_NAME_ASC]
      filter: { status: { equalTo: ACTIVE } }
    ) {
      nodes {
        id
        fullName
        memberAddress
      }
    }
  }
`);

export const CreateMeterDocument = graphql(`
  mutation DeskCreateMeter($meter: MeterInput!) {
    createMeter(input: { meter: $meter }) {
      meter {
        id
      }
    }
  }
`);

export const UpdateMeterDocument = graphql(`
  mutation DeskUpdateMeter($id: UUID!, $patch: MeterPatch!) {
    updateMeter(input: { id: $id, patch: $patch }) {
      meter {
        id
        label
        meterNo
        meterAddress
        sortOrder
        memberId
      }
    }
  }
`);

export const ChangeMeterStatusDocument = graphql(`
  mutation DeskChangeMeterStatus($input: AppChangeMeterStatusInput!) {
    appChangeMeterStatus(input: $input) {
      result {
        id
        meterStatus
        statusReason
        statusChangedAt
      }
    }
  }
`);

/** Refetch members and meters after any change to either. */
export const peopleContext = {
  additionalTypenames: [
    'Member',
    'Meter',
    'ShartPerson',
    'MemberStatusChange',
    'MeterStatusChange',
    'Photo',
  ],
};
